import React, { useState } from "react";
import { AcceptTerms } from "@/components/Legal";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth, homeFor } from "@/contexts/AuthContext";

export const GOVERNORATES = ["baghdad", "basra", "nineveh", "erbil", "sulaymaniyah", "duhok", "halabja", "kirkuk", "anbar", "babil",
  "karbala", "najaf", "wasit", "diyala", "saladin", "qadisiyyah", "maysan", "dhi_qar", "muthanna"];

const FIELDS = [
  { k: "business_name", label: "auth.businessName", type: "text" },
  { k: "phone", label: "auth.phone", type: "tel", ltr: true, ph: "07XX XXX XXXX" },
  { k: "username", label: "auth.username", type: "text", ltr: true, ph: "e.g. zawraa_2024", hint: "auth.usernameHint", min: 3 },
  { k: "password", label: "auth.password", type: "password", ltr: true, min: 8 },
];

export default function PartnerRegister() {
  const { t } = usePrefs();
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<Record<string, string>>({ governorate: "" });
  const [error, setError] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/venue/register", { ...form, accept_terms: agree });
      await refreshMe();
      navigate("/venue", { replace: true });
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const set = (k: string, v: string) => setForm({ ...form, [k]: v });

  return (
    <AuthLayout title={t("auth.registerTitle")} sub={t("auth.registerSub")}>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="venue-register-form">
        {FIELDS.map((f) => (
          <div key={f.k} className="space-y-2">
            <Label htmlFor={f.k}>{t(f.label)}</Label>
            <Input id={f.k} type={f.type} dir={f.ltr ? "ltr" : undefined} required minLength={f.min || 2} placeholder={f.ph}
              autoCapitalize={f.k === "username" ? "none" : undefined} autoComplete={f.k === "username" ? "username" : f.k === "password" ? "new-password" : undefined}
              value={form[f.k] || ""} onChange={(e: any) => set(f.k, f.k === "username" ? e.target.value.toLowerCase().replace(/\s/g, "") : e.target.value)} data-testid={`register-${f.k}-input`} className="h-12 rounded-2xl" />
            {f.hint && <p className="text-xs text-muted-foreground">{t(f.hint)}</p>}
          </div>
        ))}
        <div className="space-y-2">
          <Label htmlFor="governorate">{t("reg.governorate")}</Label>
          <select id="governorate" required value={form.governorate} onChange={(e) => set("governorate", e.target.value)} data-testid="register-governorate-select" className="h-12 w-full rounded-2xl border bg-background px-3 text-sm">
            <option value="" disabled>{t("reg.chooseGov")}</option>
            {GOVERNORATES.map((g) => <option key={g} value={g}>{t(`gov.${g}`)}</option>)}
          </select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="description">{t("reg.description")}</Label>
          <textarea id="description" rows={2} maxLength={1000} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="register-description-input" className="w-full rounded-2xl border bg-background p-3 text-sm" />
        </div>
        <AcceptTerms checked={agree} onChange={setAgree} className="sm:col-span-2" />
        {error && <p data-testid="register-error" className="sm:col-span-2 text-sm text-primary font-semibold">{error}</p>}
        <button disabled={busy} data-testid="venue-signup-submit-button" className="sm:col-span-2 h-12 rounded-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 disabled:opacity-60 transition-[background-color]">
          {busy ? t("common.loading") : t("auth.registerSubmit")}
        </button>
      </form>
      <p className="text-sm">
        {t("auth.haveAccount")}{" "}
        <Link to="/partner/login" data-testid="goto-login-link" className="text-primary font-bold">{t("auth.login")}</Link>
      </p>
    </AuthLayout>
  );
}
